import * as React from 'react';
import {Button, Dropdown, Flex, Header, Loader, Pill, PillGroup, SendIcon, Text} from '@fluentui/react-northstar';
import './sendConfirmationTaskModule.scss';
import {getDraftNotification, sendDraftNotification} from '../../apis/messageListApi';
import {getInitAdaptiveCard, updateCard} from '../AdaptiveCard/adaptiveCard';
import {defaultDraftMessage} from "../NewMessage/newMessage";
import update from "immutability-helper";
import {getBlobImage} from "../../apis/blobImageApi";
import {IDraftMessage, IMessageChannel, ISendType} from '../../Interfaces/Message';
import {IButtonMessage} from '../../Interfaces/ButtonMessage';
import {ITranslationMessage} from '../../Interfaces/TranslationMessage';
import languageObject from "../../translations";
import translations from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";
import {IGroup} from "../../Interfaces/IGroup";
import GroupApi from "../../apis/groupApi";
import {WindowsNotification} from "../WindowsNotification/WindowsNotification";
import {DelayedDispatch} from "./DelayedDispatchForm/DelayedDispatch";

export interface SendConfirmationTaskModuleProps {
    messageId: string,
    onMessageSent: () => void,
}

export interface IStatusState {
    draftMessage: IDraftMessage,
    loading: boolean;
    groups: IGroup[];
    allUsers: boolean;
    messageId: string;
    currentPreviewLanguage: number,
    sendingMessage: boolean,
    delayedDispatch: boolean,
    delayedDispatchDate: string | null,
}

class SendConfirmationTaskModule extends React.Component<SendConfirmationTaskModuleProps, IStatusState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    private readonly card: any;

    constructor(props: SendConfirmationTaskModuleProps) {
        super(props);
        this.card = getInitAdaptiveCard();

        this.state = {
            draftMessage: defaultDraftMessage,
            loading: true,
            groups: new Array<IGroup>(),
            allUsers: false,
            messageId: "",
            currentPreviewLanguage: -1,
            sendingMessage: false,
            delayedDispatch: false,
            delayedDispatchDate: null,
        };
    }

    public async componentDidMount() {
        const english = this.context.languages.list.find(language => language.code === "EN");
        if (!english) throw new Error("Can't find english language");
        this.setState({
            draftMessage: update(this.state.draftMessage, {
                defaultLanguage: {id: {$set: english.id}}
            })
        });
        let id = this.props.messageId;
        if (id) {
            this.getItem(id).then(() => {
                this.setState({
                    messageId: id,
                }, () => {
                    this.setState({loading: false}, () => this.updateCard());
                });
            });
        }
    }

    private getItem = async (id: string) => {
        try {
            const draftMessageDetail = await getDraftNotification(id);
            if (draftMessageDetail.defaultLanguage.imageLink) {
                await getBlobImage(draftMessageDetail.defaultLanguage.imageLink)
                    .then(response => draftMessageDetail.defaultLanguage.image = response)
            }
            if (draftMessageDetail.defaultLanguage.bannerLink) {
                await getBlobImage(draftMessageDetail.defaultLanguage.bannerLink)
                    .then(response => draftMessageDetail.defaultLanguage.banner = response)
            }

            // Retrieve image additional languages
            for await(let m of draftMessageDetail.translations) {
                if (m.imageLink) await getBlobImage(m.imageLink).then(response => m.image = response)
                if (m.bannerLink) await getBlobImage(m.bannerLink).then(response => m.banner = response)
            }

            // Check default language buttons
            if (!draftMessageDetail.defaultLanguage.buttons)
                draftMessageDetail.defaultLanguage.buttons = new Array<IButtonMessage>();

            // Check additional languages buttons
            draftMessageDetail.translations.forEach((al: ITranslationMessage) => {
                if (!al.buttons) al.buttons = new Array<IButtonMessage>();
            });

            const groups = new Array<IGroup>();

            if (draftMessageDetail.groupId) {
                const group = await GroupApi.getGroup(draftMessageDetail.groupId);
                groups.push(group);
            }

            this.setState({draftMessage: draftMessageDetail, groups});
        } catch (error) {
            return error;
        }
    }

    private handleSendMessage = async () => {
        this.setState({sendingMessage: true});
        await sendDraftNotification(this.state.messageId, this.state.delayedDispatch ? this.state.delayedDispatchDate : null);
        setTimeout(this.props.onMessageSent, 3000);
    }

    private handleToggleDelayedDispatch = () => {
        this.setState(prevState => ({delayedDispatch: !prevState.delayedDispatch}));
    }

    private handleDelayedDispatchDateChange = (date: string) => {
        this.setState({delayedDispatchDate: date});
    }

    private updateCard = () => {
        if (this.card) updateCard(this.card, this.state.draftMessage, this.context.topics.list,
            this.context.languages.list, this.state.currentPreviewLanguage);
    }

    public render(): JSX.Element {
        // Wait to load
        if (this.state.loading) return <Flex fill hAlign={"center"}><Loader/></Flex>;

        // Default language name
        const defaultLanguageName = this.context.languages.list
            .find(l => l.id === this.state.draftMessage.defaultLanguage.id)?.languageName ?? "";

        // Additional languages names
        const additionalLanguagesNames = new Array<string>();
        this.state.draftMessage.translations.forEach(al => {
            additionalLanguagesNames.push(this.context.languages.list.find(l => l.id === al.id)?.languageName ?? "");
        })

        // Dropdown items for preview
        const previewLanguagesItems = [this.state.draftMessage.defaultLanguage, ...this.state.draftMessage.translations]
            .map(tm => ({
                key: tm.id,
                header: languageObject.translate(this.context.languages.list.find(l => l.id === tm.id)?.languageName ?? ""),
            }));

        return (
            <Flex fill column gap="gap.small">
                <Flex fill gap="gap.small" styles={{overflow: "scroll"}}>
                    <Flex fill column styles={{maxWidth: "50%"}}>
                        <Header className="status_title" as={"h3"}
                                content={languageObject.translate("DefaultLanguage")}/>
                        <Pill className={"pill"} rectangular>{languageObject.translate(defaultLanguageName)}</Pill>
                        <Header className="status_title" as={"h3"}
                                content={languageObject.translate("AdditionalLanguages")}/>
                        {additionalLanguagesNames.length > 0 ?
                            <PillGroup>
                                {additionalLanguagesNames.map(m => (
                                    <Pill key={"message_language_" + m} rectangular
                                          className={"pill"}>{languageObject.translate(m)}</Pill>
                                ))}
                            </PillGroup>
                            :
                            <Text style={{marginLeft: "5px"}}
                                  content={languageObject.translate("NoAdditionalLanguage")}/>
                        }
                        <Header className="status_title" as={"h3"} content={languageObject.translate("Audience")}/>
                        <PillGroup>
                            <Pill rectangular className={"pill"}>
                                {this.renderAudience()}
                            </Pill>
                        </PillGroup>
                        <Header className="status_title" as={"h3"} content={languageObject.translate("Topics")}/>
                        <PillGroup>
                            {this.state.draftMessage.topicsId.map(tid => (
                                <Pill key={tid} rectangular className={"pill"}>
                                    {this.context.topics.list.find(t => t.id === tid)?.title}
                                </Pill>)
                            )}
                        </PillGroup>
                        <Header className="status_title" as={"h3"} content={languageObject.translate("Channel")}/>
                        <PillGroup>
                            <Pill key={"channel"} rectangular className={"pill"}>
                                {translations.translate(IMessageChannel[this.state.draftMessage.channel])}
                            </Pill>
                        </PillGroup>
                    </Flex>
                    <Flex fill column styles={{maxWidth: "50%"}}>
                        <Dropdown
                            fluid
                            items={previewLanguagesItems}
                            value={previewLanguagesItems[this.state.currentPreviewLanguage + 1]}
                            onChange={(e, data) => {
                                this.setState({currentPreviewLanguage: (data as any).highlightedIndex - 1}, () => this.updateCard())
                            }}
                            styles={{marginTop: "15px", marginBottom: "10px"}}
                        />
                        {this.renderMessagePreview()}
                    </Flex>
                </Flex>
                <Flex styles={{width: "100%", marginBottom: "32px"}} vAlign="center" space={"between"}>
                    <DelayedDispatch
                        disabled={this.state.sendingMessage}
                        isDelayed={this.state.delayedDispatch}
                        onToggleDelayedDispatch={this.handleToggleDelayedDispatch}
                        onDateChange={this.handleDelayedDispatchDateChange}
                    />
                    <Button
                        disabled={this.state.sendingMessage} loading={this.state.sendingMessage}
                        icon={<SendIcon outline/>}
                        content={languageObject.translate("Send")} onClick={this.handleSendMessage} primary
                    />
                </Flex>
            </Flex>
        )
    }

    public renderMessagePreview = () => {
        if ([IMessageChannel.Channel_Teams, IMessageChannel.Channel_GPTPro].includes(this.state.draftMessage.channel))
            return <div className="adaptiveCardContainer"/>;
        const language = [this.state.draftMessage.defaultLanguage, ...this.state.draftMessage.translations][this.state.currentPreviewLanguage + 1];
        if (!language) return null;
        return (
            <Flex styles={{padding: "10px"}}>
                <WindowsNotification data={language}
                                     botName={this.context.gpts.list.find(g => g.id === this.state.draftMessage.gptId)?.fullName ?? undefined}/>
            </Flex>
        )
    }

    public renderAudience = () => {
        switch (this.state.draftMessage.sendType) {
            case ISendType.EveryOne:
                return languageObject.translate("AllUsersLabel");
            case ISendType.Group:
                return this.state.groups.find(g => g.id === this.state.draftMessage.groupId)?.displayName;
            case ISendType.MailingList:
                return this.context.usersLists.lists.find(l => l.id === this.state.draftMessage.mailingListId)?.name;
            default:
                return languageObject.translate("NoAudience");
        }
    }
}

export default SendConfirmationTaskModule;